/**
 * The default practice type when an account has none set (legacy rows, or a row
 * created before the column existed). Centralised so every accessor falls back
 * to the same value rather than repeating `?? 'ATTORNEY'`.
 */
export const DEFAULT_PRACTICE_TYPE = 'ATTORNEY';
export const PRACTICE_CONFIG = {
    ATTORNEY: {
        features: {
            disbursements: true,
            ratePerKm: true,
            ratePerPage: true,
            trustAccounting: true,
            invoicing: true,
            recoveryIncomeDefault: true,
        },
        invoice: { showFeesHeading: true, dueDateSeed: null },
        reports: {
            visibleReportIds: [
                'income-statement',
                'balance-sheet',
                'trial-balance',
                'cash-flow',
                'debtors-age-analysis',
                'creditors-age-analysis',
            ],
        },
    },
    ADVOCATE: {
        features: {
            disbursements: false,
            ratePerKm: false,
            ratePerPage: false,
            trustAccounting: true,
            invoicing: true,
            recoveryIncomeDefault: true,
        },
        invoice: {
            showFeesHeading: true,
            dueDateSeed: { mode: 'END_OF_MONTH_PLUS_DAYS', days: 60 },
        },
        reports: {
            visibleReportIds: [
                'income-statement',
                'balance-sheet',
                'trial-balance',
                'cash-flow',
                'debtors-age-analysis',
                'creditors-age-analysis',
            ],
        },
    },
    BUSINESS: {
        features: {
            disbursements: true,
            ratePerKm: false,
            ratePerPage: false,
            trustAccounting: false,
            invoicing: true,
            recoveryIncomeDefault: false,
        },
        invoice: { showFeesHeading: false, dueDateSeed: null },
        reports: {
            visibleReportIds: [
                'income-statement',
                'balance-sheet',
                'trial-balance',
                'cash-flow',
                'debtors-age-analysis',
                'creditors-age-analysis',
            ],
        },
    },
    // Administered entities: books a firm keeps for a legal entity it
    // administers. No timekeeping surfaces reach an entity (the shell hides
    // them on the derived `administeredBy` signal), so the activity and
    // disbursement toggles describe what the agent and the wizard would offer
    // if they did; all three kinds turn them off, since none bills a client
    // for time or out-of-pocket spend the way a firm's own practice does.
    BUSINESS_RESCUE: {
        features: {
            disbursements: false,
            ratePerKm: false,
            ratePerPage: false,
            trustAccounting: false,
            invoicing: true,
            recoveryIncomeDefault: false,
        },
        invoice: { showFeesHeading: false, dueDateSeed: null },
        // A company in rescue keeps trading under its practitioner, so the
        // going-concern reports are exactly what the practitioner and the
        // creditors need - the same full set a firm sees.
        reports: {
            visibleReportIds: [
                'income-statement',
                'balance-sheet',
                'trial-balance',
                'cash-flow',
                'debtors-age-analysis',
                'creditors-age-analysis',
            ],
        },
    },
    INSOLVENCY: {
        // A liquidator or trustee may carry on the insolvent's business with
        // authority under Insolvency Act 24 of 1936 s 80(1).
        features: {
            disbursements: false,
            ratePerKm: false,
            ratePerPage: false,
            trustAccounting: false,
            invoicing: true,
            recoveryIncomeDefault: false,
        },
        invoice: { showFeesHeading: false, dueDateSeed: null },
        // A company being wound up reports through its liquidation and
        // distribution account, not through trading statements, so the
        // going-concern reports (income statement, balance sheet, cash flow)
        // are withheld. Trial balance stays - it proves the books balance,
        // not a going-concern position - and the age analyses stay too: they
        // are the book debts and claims inherited at sequestration, which is
        // exactly what a liquidator works from.
        reports: {
            visibleReportIds: ['trial-balance', 'debtors-age-analysis', 'creditors-age-analysis'],
        },
    },
    // A solvent deceased estate the firm administers as executor. Never trades,
    // so sales invoicing is off - money coming in is a realisation or
    // a recovery under reg 5(1), recorded through the bank and the ledger, not
    // a sale to a customer.
    ESTATE_ADMINISTRATION: {
        features: {
            disbursements: false,
            ratePerKm: false,
            ratePerPage: false,
            trustAccounting: false,
            invoicing: false,
            recoveryIncomeDefault: false,
        },
        invoice: { showFeesHeading: false, dueDateSeed: null },
        // A solvent deceased estate never trades and reports through its
        // liquidation and distribution account under reg 5 of GN R473 of
        // 1972, for the same statutory reason as INSOLVENCY - not a
        // mechanical one: the estate carries its assets in NON_CURRENT_ASSETS,
        // which maps to INVESTING, and an asset realisation genuinely IS an
        // investing activity, so this is not guarding against a mis-mapped
        // report. Trial balance and the age analyses stay for the same
        // reasons as INSOLVENCY - the age analyses here are the debts and
        // claims inherited at death, not invoices the estate raised.
        reports: {
            visibleReportIds: ['trial-balance', 'debtors-age-analysis', 'creditors-age-analysis'],
        },
    },
};
/**
 * Resolve the full app configuration for an account's practice type. The single
 * entry point that owns the nullable resolution (`?? DEFAULT_PRACTICE_TYPE`), so
 * that default lives in one place and call sites never index `PRACTICE_CONFIG`.
 *
 * Also falls back to the default row for a `practiceType` this build's
 * `PRACTICE_CONFIG` does not recognise. A Prisma migration that adds a new
 * `PracticeType` enum value lands before the Cloud Run rollout, and a Bill
 * browser tab that has not reloaded keeps running the old bundle - with the
 * old, shorter `PRACTICE_CONFIG` - for as long as that tab stays open. Without
 * this fallback, any account stamped with the new value during that window
 * throws a `TypeError` in every component that resolves practice config
 * (`Sidebar`, `InvoicingRoute`, the ledger and account routes), crashing the
 * app shell itself. Falling back keeps an old tab alive on default behaviour
 * until it reloads and picks up the real config; it does not fix the crash for
 * a tab that never reloads, and it is not a substitute for ordering a breaking
 * enum change as a rollout stack - it is the cheap, permanent guard underneath
 * that ordering, and it also covers every future value added to this enum.
 */
export function getPracticeConfig(practiceType) {
    const resolved = practiceType ?? DEFAULT_PRACTICE_TYPE;
    return PRACTICE_CONFIG[resolved] ?? PRACTICE_CONFIG[DEFAULT_PRACTICE_TYPE];
}
// -- Slice accessors ---------------------------------------------------------
// Conveniences for whole slices (a cohesive sub-config), NOT a getter per
// property: read a single field straight off the config
// (`getPracticeConfig(pt).invoice.dueDateSeed`), don't add a getter for it.
/** The capability toggles for a practice type. */
export function getPracticeFeatures(practiceType) {
    return getPracticeConfig(practiceType).features;
}
/** Which ledger reports a practice type sees. */
export function getPracticeReports(practiceType) {
    return getPracticeConfig(practiceType).reports;
}
